import {
  HandlerType,
  ProgressBarStyleType,
  ScaleType,
  SliderValueType,
} from './types';

const getLogValues = (
  minValue: number,
  maxValue: number,
): {
  minPosition: number;
  maxPosition: number;
  minLogValue: number;
  maxLogValue: number;
  adjustScale: number;
} => {
  const minPosition = 0;
  const maxPosition = 100;

  const minLogValue = Math.log((minValue < 100 ? 100 : minValue) / 100);
  const maxLogValue = Math.log(maxValue / 100);

  return {
    minPosition,
    maxPosition,
    minLogValue,
    maxLogValue,
    adjustScale: (maxLogValue - minLogValue) / (maxPosition - minPosition),
  };
};

export const getProgressBarStyle = ({
  rightHandlerX,
  leftHandlerX,
}: {
  rightHandlerX: number;
  leftHandlerX: number;
}): ProgressBarStyleType => {
  // calculate relative required width
  const relativeWidth = rightHandlerX - leftHandlerX;

  return {
    width: `${relativeWidth}%`,
    left: `${leftHandlerX}%`,
  };
};

/*
 *   Gets Relative distance (%) from the left side of a DOM element
 *   to where a click event happens
 */
export const getRelativeX = ({
  eventX,
  componentX,
  componentWidth,
}: {
  eventX: number;
  componentX: number;
  componentWidth: number;
}): number => {
  const diffX = eventX - componentX;
  const relativeX = (diffX / componentWidth) * 100;
  return Math.min(100, Math.max(0, relativeX));
};

export const getClosestHandlerToClick = ({
  relativeX,
  leftHandlerX,
  rightHandlerX,
}: {
  relativeX: number;
  leftHandlerX: number;
  rightHandlerX: number;
}): HandlerType => {
  const distanceToLeftHandler = Math.abs(relativeX - leftHandlerX);
  const distanceToRightHandler = Math.abs(relativeX - rightHandlerX);
  return distanceToLeftHandler <= distanceToRightHandler ? 'left' : 'right';
};

export const getNumericValue = ({
  scale,
  relativeX,
  maxValue,
  minValue,
  step,
}: {
  scale: ScaleType;
  relativeX: number;
  maxValue: number;
  minValue: number;
  step: number;
}): number => {
  if (scale === 'log') {
    const {minPosition, minLogValue, adjustScale} = getLogValues(
      minValue,
      maxValue,
    );

    const exp = minLogValue + adjustScale * (relativeX - minPosition);
    return Math.round(Math.exp(exp)) * 100;
  } else {
    const numericValue = (relativeX / 100) * (maxValue - minValue) + minValue;

    return Math.round(numericValue / step) * step;
  }
};

export const getIntersectionRelativeX = ({
  scale,
  values,
  handler,
  minDistance,
  maxValue,
  minValue,
  step,
}: {
  scale: ScaleType;
  values: SliderValueType;
  handler: HandlerType;
  minDistance: number;
  maxValue: number;
  minValue: number;
  step: number;
}): number => {
  const [lowValue, highValue] = values;
  const isLeftHandler = handler === 'left';
  let intersectionValue = 0;

  if (scale === 'log') {
    const highRelativeX = getRelativeXFromValue({
      scale: 'log',
      value: highValue,
      maxValue,
      minValue,
    });

    const lowRelativeX = getRelativeXFromValue({
      scale: 'log',
      value: lowValue,
      maxValue,
      minValue,
    });

    if (isLeftHandler) {
      intersectionValue =
        getNumericValue({
          scale: 'linear',
          relativeX: highRelativeX,
          maxValue,
          minValue,
          step,
        }) - minDistance;
    } else {
      intersectionValue =
        getNumericValue({
          scale: 'linear',
          relativeX: lowRelativeX,
          maxValue,
          minValue,
          step,
        }) + minDistance;
    }
  } else {
    intersectionValue = isLeftHandler
      ? highValue - minDistance
      : lowValue + minDistance;
  }

  return getRelativeXFromValue({
    scale: 'linear',
    value: intersectionValue,
    maxValue,
    minValue,
  });
};

export const getIntersectionValue = ({
  componentX,
  componentWidth,
  intersectionPercentage,
  maxValue,
  minValue,
  scale = 'linear',
  step,
}: {
  componentX: number;
  componentWidth: number;
  intersectionPercentage: number;
  minValue: number;
  maxValue: number;
  scale?: ScaleType;
  step: number;
}): number => {
  // Simulates a displacement, therefor the + x
  const minDistance = componentWidth * intersectionPercentage + componentX;
  const relativeMinX = getRelativeX({
    eventX: minDistance,
    componentX,
    componentWidth,
  });

  /**
   * This function help to get the intersaction value between the two slider dots.
   * In order to get the proper value we need to consider the scale type of the slider (linear or logarithmic).
   * The getNumericValue function contains all the logic to handle these things
   */
  return getNumericValue({
    scale,
    relativeX: relativeMinX,
    maxValue,
    minValue,
    step,
  });
};

export const getRelativeXFromValue = ({
  scale,
  value,
  maxValue,
  minValue,
}: {
  scale: ScaleType;
  value: number;
  maxValue: number;
  minValue: number;
}): number => {
  if (minValue && minValue >= value) {
    return 0;
  }

  const relativeX = (() => {
    if (scale === 'log') {
      const {minPosition, minLogValue, adjustScale} = getLogValues(
        minValue,
        maxValue,
      );

      return (
        (Math.log((value < 100 ? 100 : value) / 100) - minLogValue) /
          adjustScale +
        minPosition
      );
    }

    return ((value - minValue) / (maxValue - minValue)) * 100;
  })();

  return relativeX;
};

export const areHandlersIntersecting = ({
  scale,
  relativeX,
  max,
  min,
  values,
  minHandlerDistance,
  handler,
  step,
}: {
  scale: ScaleType;
  relativeX: number;
  max: number;
  min: number;
  values: SliderValueType;
  minHandlerDistance: number;
  handler: HandlerType;
  step: number;
}): boolean => {
  const [lowValue, highValue] = values;
  const isRightHandler = handler === 'right';

  let minValue = 0;
  let maxValue = 0;

  const numericValue = getNumericValue({
    scale: 'linear',
    relativeX,
    maxValue: max,
    minValue: min,
    step,
  });

  if (scale === 'log') {
    minValue = isRightHandler
      ? getNumericValue({
          scale: 'linear',
          relativeX: getRelativeXFromValue({
            scale: 'log',
            value: lowValue,
            maxValue: max,
            minValue: min,
          }),
          maxValue: max,
          minValue: min,
          step,
        })
      : numericValue;

    maxValue = isRightHandler
      ? numericValue
      : getNumericValue({
          scale: 'linear',
          relativeX: getRelativeXFromValue({
            scale: 'log',
            value: highValue,
            maxValue: max,
            minValue: min,
          }),
          maxValue: max,
          minValue: min,
          step,
        });
  } else {
    minValue = isRightHandler ? lowValue : numericValue;
    maxValue = isRightHandler ? numericValue : highValue;
  }

  const handlersDistance = maxValue - minValue;
  return handlersDistance < minHandlerDistance;
};

export const getValuesInValidRange = ({
  values,
  min,
  max,
  intersectionValue,
}: {
  values: SliderValueType;
  min: number;
  max: number;
  intersectionValue: number;
}): SliderValueType => {
  const oldLowValue = values[0];
  const oldHighValue = values[1];

  if (oldLowValue <= min && oldHighValue <= min) {
    return [min, min + intersectionValue];
  }

  if (oldLowValue >= max && oldHighValue >= max) {
    return [max - intersectionValue, max];
  }

  if (oldHighValue > max && oldLowValue < max) {
    const newHigh = max;
    const intersecting = newHigh - oldLowValue < intersectionValue;
    const newLow = intersecting ? max - intersectionValue : oldLowValue;
    return [newLow, newHigh];
  }

  if (oldLowValue < min && oldHighValue > min) {
    const newLow = min;
    const intersecting = oldHighValue - newLow < intersectionValue;
    const newHigh = intersecting ? min + intersectionValue : oldHighValue;
    return [newLow, newHigh];
  }

  return [oldLowValue, oldHighValue];
};
